import type { JSONContent } from '@tiptap/core';
import escapeRegExp from 'lodash/escapeRegExp';
import type { InvoiceComment, InvoiceCommentPerson } from 'types/invoiceComments';

export type CommentNode = { comment: InvoiceComment; replies: CommentNode[] };

/** The top-level comments newest first, each holding its replies oldest first, as a conversation reads. */
export function buildThreads(comments: InvoiceComment[]): CommentNode[] {
    const nodes = new Map(comments.map((comment) => [comment.id, { comment, replies: [] as CommentNode[] }]));
    const roots: CommentNode[] = [];
    // The API lists the entries oldest first, so each reply list is already in reading order.
    for (const node of nodes.values()) {
        const parent = node.comment.parentId === null ? undefined : nodes.get(node.comment.parentId);
        (parent ? parent.replies : roots).push(node);
    }
    return roots.reverse();
}

/** The replies under an entry at every depth that are not deleted: the ones that stay when it is deleted. */
export function liveReplies(node: CommentNode): number {
    return node.replies.reduce((sum, reply) => sum + (reply.comment.deleted ? 0 : 1) + liveReplies(reply), 0);
}

/** The top-level comments still open: a reply never counts, nor a deleted entry. */
export function countOpen(comments: InvoiceComment[] | undefined): number {
    return comments?.filter((c) => c.parentId === null && !c.deleted && c.handledAt === null).length ?? 0;
}

/** A tag's `employeeId` is null when the label does not name one employee: two tagged people share it. */
export type TextSegment = { text: string; tag: false } | { text: string; tag: true; employeeId: number | null };

/** The text cut into plain runs and tags, `@` and a tag's label, the longest label tried first. */
export function tagSegments(text: string, mentions: InvoiceCommentPerson[]): TextSegment[] {
    const idByName = new Map<string, number | null>();
    for (const { id, name } of mentions) {
        if (name) idByName.set(name, idByName.has(name) && idByName.get(name) !== id ? null : (id ?? null));
    }
    const names = [...idByName.keys()].sort((a, b) => b.length - a.length);
    if (names.length === 0) {
        return [{ text, tag: false }];
    }
    // A label ends at a word's end: a tag under the label "Mari" is not the start of "@Marianne".
    const tag = new RegExp(`@(?:${names.map(escapeRegExp).join('|')})(?![\\p{L}\\p{N}])`, 'gu');
    const segments: TextSegment[] = [];
    let from = 0;
    for (const match of text.matchAll(tag)) {
        if (match.index > from) segments.push({ text: text.slice(from, match.index), tag: false });
        segments.push({ text: match[0], tag: true, employeeId: idByName.get(match[0].slice(1)) ?? null });
        from = match.index + match[0].length;
    }
    if (from < text.length) segments.push({ text: text.slice(from), tag: false });
    return segments;
}

/**
 * The name a tag's label is cut from, as the server checks it: the first and last names with their outer spaces
 * trimmed, the empty ones left out, joined by one space.
 */
export function tagName({ firstName, lastName }: { firstName?: string | null; lastName?: string | null }): string {
    return [firstName, lastName]
        .map((part) => part?.replace(/^ +| +$/g, ''))
        .filter(Boolean)
        .join(' ');
}

const MAX_QUERY_LENGTH = 40;

/**
 * The @-query typed up to the caret: an `@` at the start or after white space, then a letter, and no line break
 * since. A name holds spaces, so the query may too.
 */
export function mentionQueryAt(text: string, caret: number): { start: number; query: string } | null {
    const before = text.slice(0, caret);
    const start = before.lastIndexOf('@');
    if (start < 0 || (start > 0 && !/\s/.test(before[start - 1]))) {
        return null;
    }
    const query = before.slice(start + 1);
    if (!/^\p{L}/u.test(query) || query.includes('\n') || query.length > MAX_QUERY_LENGTH) {
        return null;
    }
    return { start, query };
}

/**
 * A saved text as the composer's document: a paragraph per line, each tag a tag node. A label two tagged people
 * share goes to each of them in turn, which nobody can see as both read the same; a tag left over is plain text.
 */
export function commentDoc(text: string, mentions: InvoiceCommentPerson[]): JSONContent {
    const idsByLabel = new Map<string, number[]>();
    for (const { id, name } of mentions) idsByLabel.set(name, [...(idsByLabel.get(name) ?? []), id]);
    return {
        type: 'doc',
        content: text.split('\n').map((line) => ({
            type: 'paragraph',
            content: tagSegments(line, mentions)
                .filter((segment) => segment.text !== '')
                .map((segment) => {
                    const label = segment.text.slice(1);
                    const employeeId = segment.tag ? (segment.employeeId ?? idsByLabel.get(label)?.shift()) : undefined;
                    return employeeId === undefined
                        ? { type: 'text', text: segment.text }
                        : { type: 'mention', attrs: { employeeId, label } };
                }),
        })),
    };
}
